import React, { createContext, useState, useCallback, useRef } from 'react';
import { GridType, GameResult } from '../types';
import { FIXED_GAME_CONFIG } from '../constants/fixedGameConfig.ts';
import { FIRESWEEPER_CONTAINER } from '../constants/gameModes.ts';
import { fireSpreadService } from '../services/fireSpreadService.ts';
import { gameService } from '../services/gameService.ts';
import { useNavigate } from 'react-router-dom';
import { getDailySeed, getUtcDayKey } from '../simulation/dailySeed.ts';
import { createSeededGameRng } from '../simulation/seededGame.ts';

export type ContainerLeaderboardLocationState = {
  playerResult: GameResult;
};

interface GameContextType {
  grid: GridType;
  config: typeof FIXED_GAME_CONFIG;
  isGameStarted: boolean;
  isGameOver: boolean;
  isPlayerTurn: boolean;
  survivalPercentage: number;
  gameStartTime: number;
  dayKey: string;
  seed: number;
  preBurns: number;
  gameResult: 'win' | 'lose' | null;
  startGame: () => void;
  resetGame: () => void;
  burnCell: (row: number, col: number) => void;
}

export const GameContext = createContext<GameContextType | null>(null);

const config = FIXED_GAME_CONFIG;

const calculateDuration = (startTime: number): number => {
  if (startTime === 0) return 0;
  return Math.round((Date.now() - startTime) / 1000);
};

export function GameProvider({ children }: { children: React.ReactNode }) {
  const [grid, setGrid] = useState<GridType>([]);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [survivalPercentage, setSurvivalPercentage] = useState(100);
  const [dayKey, setDayKey] = useState(getUtcDayKey());
  const [seed, setSeed] = useState(0);
  const [gameStartTime, setGameStartTime] = useState<number>(0);
  const [gameResult, setGameResult] = useState<'win' | 'lose' | null>(null);
  const preBurnsRef = useRef(0);
  const [preBurns, setPreBurns] = useState(0);
  const dayKeyRef = useRef(getUtcDayKey());
  const seedRef = useRef(0);
  const rngRef = useRef<(() => number) | null>(null);
  const isPlayerTurnRef = useRef(true);
  const gridRef = useRef<GridType>([]);
  const isFinishingRef = useRef(false);
  const navigate = useNavigate();

  const finishGame = useCallback(
    (afterFireGrid: GridType, gameStatus: 'win' | 'lose') => {
      if (isFinishingRef.current) return;
      isFinishingRef.current = true;

      const afterFireSurvival = fireSpreadService.calculateSurvival(afterFireGrid);
      gridRef.current = afterFireGrid;
      setGrid(afterFireGrid);
      setPreBurns(preBurnsRef.current);
      setSurvivalPercentage(afterFireSurvival);
      setIsGameOver(true);
      setIsGameStarted(false);
      setIsPlayerTurn(false);
      isPlayerTurnRef.current = false;
      setGameResult(gameStatus);

      const result: GameResult = {
        survivalPercentage: afterFireSurvival,
        duration: calculateDuration(gameStartTime),
        timestamp: Date.now(),
        gameType: 'Turn Based',
        startingFires: config.startingFires,
        preBurns: preBurnsRef.current,
        config,
        finalGrid: afterFireGrid,
        result: gameStatus,
        mode: 'survival',
        dayKey: dayKeyRef.current,
        seed: seedRef.current,
      };

      void gameService
        .saveSurvivalResult(result)
        .then((id) => {
          navigate(FIRESWEEPER_CONTAINER.leaderboardPath, {
            replace: true,
            state: { playerResult: { ...result, id } } satisfies ContainerLeaderboardLocationState,
          });
        })
        .catch((error) => {
          console.error(error);
          navigate(FIRESWEEPER_CONTAINER.leaderboardPath, {
            replace: true,
            state: { playerResult: result } satisfies ContainerLeaderboardLocationState,
          });
        });
    },
    [gameStartTime, navigate]
  );

  const startGame = useCallback(() => {
    const today = getUtcDayKey();
    const dailySeed = getDailySeed('container', today);
    const { rng, firePositions } = createSeededGameRng(
      dailySeed,
      config.gridSize,
      config.startingFires
    );
    const initializedGrid = fireSpreadService.initializeGridFromPositions(
      fireSpreadService.createGrid(config.gridSize),
      firePositions
    );

    dayKeyRef.current = today;
    seedRef.current = dailySeed;
    rngRef.current = rng;
    isPlayerTurnRef.current = true;
    isFinishingRef.current = false;
    gridRef.current = initializedGrid;
    setDayKey(today);
    setSeed(dailySeed);
    setGrid(initializedGrid);
    setIsGameStarted(true);
    setIsGameOver(false);
    setGameResult(null);
    setIsPlayerTurn(true);
    setSurvivalPercentage(fireSpreadService.calculateSurvival(initializedGrid));
    preBurnsRef.current = 0;
    setPreBurns(0);
    setGameStartTime(Date.now());
  }, []);

  const resetGame = useCallback(() => {
    setIsGameStarted(false);
    setIsGameOver(false);
    setIsPlayerTurn(true);
    setSurvivalPercentage(100);
    setGameResult(null);
    setGrid([]);
    preBurnsRef.current = 0;
    setPreBurns(0);
    setGameStartTime(0);
    rngRef.current = null;
    isPlayerTurnRef.current = true;
    isFinishingRef.current = false;
    gridRef.current = [];
  }, []);

  const burnCell = useCallback(
    (row: number, col: number) => {
      if (!isGameStarted || isGameOver || !isPlayerTurnRef.current || isFinishingRef.current) return;

      const currentGrid = gridRef.current;
      if (currentGrid[row]?.[col] !== 'alive') return;

      isPlayerTurnRef.current = false;
      setIsPlayerTurn(false);

      const burnedGrid = currentGrid.map((r) => [...r]);
      burnedGrid[row][col] = 'burned';
      preBurnsRef.current += 1;

      const rng = rngRef.current ?? Math.random;
      const afterFireGrid = fireSpreadService.updateGrid(burnedGrid, config, rng);

      gridRef.current = afterFireGrid;
      setGrid(afterFireGrid);
      setPreBurns(preBurnsRef.current);
      setSurvivalPercentage(fireSpreadService.calculateSurvival(afterFireGrid));

      const gameStatus = fireSpreadService.checkGameOver(afterFireGrid);
      if (gameStatus) {
        finishGame(afterFireGrid, gameStatus);
        return;
      }

      isPlayerTurnRef.current = true;
      setIsPlayerTurn(true);
    },
    [isGameStarted, isGameOver, finishGame]
  );

  return (
    <GameContext.Provider
      value={{
        grid,
        config,
        isGameStarted,
        isGameOver,
        isPlayerTurn,
        survivalPercentage,
        gameStartTime,
        dayKey,
        seed,
        preBurns,
        gameResult,
        startGame,
        resetGame,
        burnCell,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}
